import React, { useCallback, useState, useEffect } from 'react';
import { CalendarDaysIcon } from '@heroicons/react/24/outline';
import { bookingApi } from '../../../utils/bookingApi';
import { formatDate } from '../../../utils/dates';
import { PageHeader, Sheet, Skeleton, EmptyState, ErrorState, useConfirm, useToast } from '../../../components/ui';
import '../booking-shared.css';

const EMPTY = { startDate: '', endDate: '', reason: '' };

// /booking/admin/closures: dates the club is shut. Adding one cancels every
// session in it and gives families credits.
export default function AdminClosures() {
  const confirm = useConfirm();
  const toast = useToast();
  const [closures, setClosures] = useState(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [listError, setListError] = useState(null);

  const load = useCallback(() => {
    setLoadFailed(false);
    return bookingApi.getClosures().then(res => setClosures(res.data)).catch(() => setLoadFailed(true));
  }, []);
  useEffect(() => { load(); }, [load]);

  const open = () => { setForm(EMPTY); setError(null); setAdding(true); };
  const set = (key) => (e) => setForm(f => ({ ...f, [key]: e.target.value }));

  const handleSubmit = async () => {
    if (!form.startDate || !form.endDate || !form.reason.trim()) {
      setError('Enter the start date, end date and reason.');
      return;
    }
    if (!(await confirm({
      title: `Close from ${formatDate(form.startDate)} to ${formatDate(form.endDate)}?`,
      message: 'Every session in these dates is cancelled and families get credits for their bookings.',
      confirmLabel: 'Cancel sessions',
    }))) return;
    setSubmitting(true);
    setError(null);
    try {
      await bookingApi.createClosure(form);
      setAdding(false);
      toast(`${form.reason.trim()} added`);
      load();
    } catch (err) {
      setError(err.response?.data?.error || "Couldn't add the closure.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (c) => {
    if (!(await confirm({
      title: `Delete ${c.reason}?`,
      message: 'The dates open again for new sessions. Sessions it already cancelled stay cancelled.',
    }))) return;
    setListError(null);
    try {
      await bookingApi.deleteClosure(c.id);
      toast(`${c.reason} deleted`);
      load();
    } catch (err) {
      setListError(err.response?.data?.error || "Couldn't delete the closure.");
    }
  };

  const addButton = { label: 'Add a closure', short: 'Add', onClick: open };

  return (
    <div className="bk-page bk-page--wide">
      <PageHeader back={{ to: '/admin-hub', label: 'Admin' }} title="Closures" action={closures ? addButton : undefined} />

      {loadFailed && <ErrorState title="Couldn't load closures" onRetry={load} />}
      {!closures && !loadFailed && <Skeleton rows={3} label="Loading closures…" />}
      {listError && <p className="bk-form-error" role="alert">{listError}</p>}

      {closures?.length === 0 && (
        <EmptyState icon={CalendarDaysIcon} title="No closures yet" text="Add the dates the club is shut, such as Christmas, and those sessions are cancelled for you."
          action={<button type="button" className="bk-btn bk-btn--primary" onClick={open}>Add a closure</button>} />
      )}
      {closures?.map(c => (
        <div key={c.id} className="bk-card bk-row bk-row--between">
          <div>
            <strong>{c.reason}</strong>
            <p className="bk-small" style={{ margin: '0.2rem 0' }}>
              {formatDate(c.startDate)} to {formatDate(c.endDate)}
            </p>
          </div>
          <button type="button" onClick={() => handleDelete(c)} className="bk-btn bk-btn--outline-danger bk-btn--sm">Delete</button>
        </div>
      ))}

      {adding && (
        <Sheet
          title="Add a closure"
          onClose={() => setAdding(false)}
          dirty={form.startDate !== '' || form.endDate !== '' || form.reason !== ''}
          onSubmit={handleSubmit}
          footer={(requestClose) => (
            <div className="bk-actions">
              <button type="submit" disabled={submitting} className="bk-btn bk-btn--primary">
                {submitting ? 'Adding…' : 'Add closure'}
              </button>
              <button type="button" className="bk-btn" onClick={requestClose}>Cancel</button>
            </div>
          )}
        >
          <p className="ui-sheet__message">Every session in these dates is cancelled and families get credits.</p>
          <label className="bk-label">Start date
            <input type="date" value={form.startDate} onChange={set('startDate')} required className="bk-input" />
          </label>
          <label className="bk-label">End date
            <input type="date" value={form.endDate} min={form.startDate} onChange={set('endDate')} required className="bk-input" />
          </label>
          <label className="bk-label">Reason
            <input type="text" value={form.reason} onChange={set('reason')} required placeholder="For example, Christmas 2026" className="bk-input" />
          </label>
          {error && <p className="bk-form-error" role="alert">{error}</p>}
        </Sheet>
      )}
    </div>
  );
}
